import { AccountOverview, Vault, VaultDeposit } from "@decibeltrade/sdk";

import { WithSubaccount } from "@/contexts/queries/vault/types";

import { PledgedCollateral } from "./account-overview";
import { amountToChainUnits, getTotalPnl } from "./math";

const WEBSITE_URL_INDEX = 0;
const X_URL_INDEX = 1;
const DISCORD_URL_INDEX = 2;

/**
 * Validates if a string is a valid X (Twitter) handle or URL
 * @param value - The value to validate
 * @returns true if valid X handle or URL, false otherwise
 */
export const isValidXHandleOrUrl = (value: string): boolean => {
  if (!value || typeof value !== "string" || value.trim() === "") {
    return false;
  }

  const trimmed = value.trim();
  // Accept either a full x.com URL, or a valid handle (letters/numbers/underscores, 1-15 chars, no slashes)
  const isXUrl = /^(https?:\/\/)?(www\.)?x\.com\/[A-Za-z0-9_]{1,15}\/?$/.test(trimmed);
  const isTwitterUrl = /^(https?:\/\/)?(www\.)?twitter\.com\/[A-Za-z0-9_]{1,15}\/?$/.test(trimmed);
  const isHandle = /^[A-Za-z0-9_]{1,15}$/.test(trimmed);
  return isXUrl || isTwitterUrl || isHandle;
};

/**
 * Validates if a string is a valid Discord invite URL or code
 * @param value - The value to validate
 * @returns true if valid Discord URL or invite code, false otherwise
 */
export const isValidDiscordHandleOrUrl = (value: string): boolean => {
  if (!value || typeof value !== "string" || value.trim() === "") {
    return false;
  }

  const trimmed = value.trim();
  // Accept a full discord.gg/com/invite link or just an invite code (alphanumeric, 1-32 chars, no slashes)
  return (
    /^https?:\/\/(www\.)?discord\.gg\/[A-Za-z0-9]+$/.test(trimmed) ||
    /^https?:\/\/(www\.)?discord\.com\/invite\/[A-Za-z0-9]+$/.test(trimmed) ||
    /^(discord\.gg\/[A-Za-z0-9]+)$/.test(trimmed) ||
    /^(discord\.com\/invite\/[A-Za-z0-9]+)$/.test(trimmed) ||
    /^[A-Za-z0-9]+$/.test(trimmed) // this covers the invite code as well
  );
};

export const isValidWebsiteUrl = (value: string): boolean => {
  if (!value || typeof value !== "string" || value.trim() === "") {
    return false;
  }

  const trimmed = value.trim();
  // Accept a full URL with protocol or just a hostname (e.g. "app.decibel.trade")
  return /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/.test(trimmed);
};

export const generateVaultSocialLinks = ({
  x,
  discord,
  website,
}: {
  x?: string;
  discord?: string;
  website?: string;
}) => {
  const res = [];

  res[WEBSITE_URL_INDEX] = website ?? "";
  res[X_URL_INDEX] = x ?? "";
  res[DISCORD_URL_INDEX] = discord ?? "";

  return res;
};

export interface VaultSocials {
  xUrl: string | undefined;
  xHandle: string | undefined;
  discordUrl: string | undefined;
  discordCode: string | undefined;
  websiteUrl: string | undefined;
  websiteHost: string | undefined;
}

export const getVaultSocials = (socialLinks: string[]): VaultSocials | null => {
  let website = socialLinks[WEBSITE_URL_INDEX];
  let x = socialLinks[X_URL_INDEX];
  let discord = socialLinks[DISCORD_URL_INDEX];

  if (x && !isValidXHandleOrUrl(x)) x = "";
  if (discord && !isValidDiscordHandleOrUrl(discord)) discord = "";
  if (website && !isValidWebsiteUrl(website)) website = "";

  const xHandle = x ? getXHandle(x) : undefined;
  const discordCode = discord ? getDiscordInviteCode(discord) : undefined;
  const websiteHost = website ? getWebsiteHost(website) : undefined;

  if (!xHandle && !discordCode && !websiteHost) return null;

  return {
    xUrl: xHandle ? `https://x.com/${xHandle}` : undefined,
    xHandle,
    discordUrl: discordCode ? `https://discord.gg/${discordCode}` : undefined,
    discordCode,
    websiteUrl: websiteHost ? `https://${websiteHost}` : undefined,
    websiteHost,
  };
};

const getXHandle = (x: string): string | undefined => {
  if (!x.includes("/")) return x;
  const parts = x.replace(/\/+$/, "").split("/");
  return parts[parts.length - 1];
};

const getWebsiteHost = (website: string): string | undefined => {
  const trimmed = website.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
};

const getDiscordInviteCode = (discord: string): string | undefined => {
  const trimmed = discord.trim();

  if (/^[A-Za-z0-9]+$/.test(trimmed)) {
    return trimmed;
  }

  const match = /discord(?:\.gg|\.com\/invite)\/([A-Za-z0-9]+)/i.exec(trimmed);
  if (match?.[1]) {
    return match[1];
  }

  return undefined;
};

const PROTOCOL_VAULT_DEFAULTS = {
  description:
    "The Decibel Liquidity Provider (DLP) is the protocol's core liquidity vault, serving a dual role as both market maker and backstop liquidator. It actively provides liquidity to Decibel's order book, earning trading revenue from spreads, while also stepping in to absorb positions from liquidated accounts and capturing a portion of liquidation margins. In exchange for this yield, DLP depositors take on the risk of adverse market moves and potential losses during periods of extreme volatility. Deposits are subject to a 3-day lockup period. The DLP is essential to the health of the exchange — ensuring deep liquidity for traders and a reliable counterparty for defaulting accounts.",
  socials: {
    xUrl: "https://x.com/DecibelTrade",
    xHandle: "DecibelTrade",
    discordUrl: "https://discord.gg/decibel",
    discordCode: "decibel",
    websiteUrl: "https://app.decibel.trade",
    websiteHost: "app.decibel.trade",
  } satisfies VaultSocials,
};

export interface VaultProfile {
  description: string | undefined;
  socials: VaultSocials | null;
}

export const getVaultProfile = (vault: Vault): VaultProfile => {
  if (vault.vault_type === "protocol") {
    return {
      description: PROTOCOL_VAULT_DEFAULTS.description,
      socials: PROTOCOL_VAULT_DEFAULTS.socials,
    };
  }

  return {
    description: vault.description ?? undefined,
    socials: vault.social_links ? getVaultSocials(vault.social_links) : null,
  };
};

/**
 * Computes the unlock timestamp for a vault deposit based on the vault's lockdown period.
 * Returns null if the vault has no lockdown.
 */
export const getUnlockTimestampMs = (lockdownPeriodS: number | null | undefined): number | null =>
  lockdownPeriodS != null && lockdownPeriodS > 0 ? Date.now() + lockdownPeriodS * 1000 : null;

/**
 * On-chain, deposits whose unlock time falls within 10% of the lockdown duration
 * of the last locked entry are merged (amount added, original unlock time kept).
 * This mirrors vault_share_asset::add_lockup_entry combining logic.
 */
const LOCKUP_COMBINING_PERCENT = 10;

export const mergeOrAppendDeposit = (
  existingDeposits: VaultDeposit[] | null | undefined,
  newDeposit: VaultDeposit,
  lockdownPeriodS: number | null | undefined,
): VaultDeposit[] => {
  if (!existingDeposits?.length || !newDeposit.unlock_timestamp_ms || !lockdownPeriodS) {
    return [newDeposit, ...(existingDeposits ?? [])];
  }

  const thresholdMs = (lockdownPeriodS * 1000 * LOCKUP_COMBINING_PERCENT) / 100;
  const lastEntry = existingDeposits[0];

  if (
    lastEntry?.unlock_timestamp_ms &&
    newDeposit.unlock_timestamp_ms <= lastEntry.unlock_timestamp_ms + thresholdMs
  ) {
    const merged: VaultDeposit = {
      ...lastEntry,
      amount_usdc: lastEntry.amount_usdc + newDeposit.amount_usdc,
      shares_received: lastEntry.shares_received + newDeposit.shares_received,
    };
    return [merged, ...existingDeposits.slice(1)];
  }

  return [newDeposit, ...existingDeposits];
};

export interface UnlockingEntry {
  timestamp: number;
  amount: number;
}

export const getUnlockingEntries = (
  deposits: Array<{ amount_usdc: number; unlock_timestamp_ms: number | null }> | null | undefined,
): UnlockingEntry[] => {
  if (!deposits) return [];

  const now = Date.now();

  // Backend returns separate rows per contribution, but on-chain combining gives
  // multiple rows the same unlock_timestamp_ms. Group by timestamp to deduplicate.
  const grouped: Record<number, number> = {};
  for (const d of deposits) {
    if (d.unlock_timestamp_ms != null && d.unlock_timestamp_ms > now) {
      grouped[d.unlock_timestamp_ms] = (grouped[d.unlock_timestamp_ms] ?? 0) + d.amount_usdc;
    }
  }

  return Object.entries(grouped)
    .map(([ts, amount]) => ({ timestamp: Number(ts), amount }))
    .sort((a, b) => a.timestamp - b.timestamp);
};

export interface WithdrawableAmounts {
  totalLocked: number;
  userUnlocked: number;
  totalWithdrawable: number;
  isLiquidityCapped: boolean;
}

export const getWithdrawableAmounts = (
  totalDeposits: number,
  lockedAmount: number | null | undefined,
  maxSyncRedemption: number | undefined,
): WithdrawableAmounts => {
  const totalLocked = lockedAmount ?? 0;
  const userUnlocked = Math.max(0, totalDeposits - totalLocked);
  const totalWithdrawable =
    maxSyncRedemption !== undefined ? Math.min(userUnlocked, maxSyncRedemption) : userUnlocked;
  const isLiquidityCapped = maxSyncRedemption !== undefined && maxSyncRedemption < userUnlocked;

  return { totalLocked, userUnlocked, totalWithdrawable, isLiquidityCapped };
};

export type UnpledgePlan =
  /** Enough free shares already sit in the primary store — redeem straight away. */
  | { kind: "proceed" }
  /** Move this many base units of share out of cross margin, then redeem. */
  | { kind: "unpledge"; units: number }
  /** Enough is already draining through the withdrawal queue — wait, don't re-send. */
  | { kind: "queued" }
  /** Margin will not release enough; nothing to do but tell the user why. */
  | { kind: "locked" };

/**
 * What a vault redemption must do about shares pledged as cross-margin collateral.
 *
 * `vault_share_asset::lock_for_redemption` measures against the holder's primary
 * fungible store only, so pledged shares are invisible to it and the redeem aborts
 * with a bare `0x1`. Anything short has to come back out of margin first.
 *
 * `unlockedShares` must be the redeemable balance (`get_user_unlocked_balance`),
 * NOT the share token balance — the two differ for the whole contribution-lockup
 * window, and the token balance understates the shortfall.
 *
 * Decided in base units, because that is the comparison the chain makes: the redeem
 * locks `amountToChainUnits(shares)`, and `shares` usually arrives as
 * `amount / sharePrice`. Subtracting the floats and rounding up turns 1e-10 of noise
 * into a whole extra base unit, which asks margin for more than `withdrawable`.
 */
export const planShareUnpledge = ({
  shares,
  unlockedShares,
  pledged,
  pendingUnpledgeUnits = 0,
}: {
  shares: number;
  unlockedShares: number;
  pledged: PledgedCollateral | undefined;
  /**
   * Share base units already released from collateral and working through the
   * perp withdrawal queue. Chain units, matching the returned `units`.
   */
  pendingUnpledgeUnits?: number;
}): UnpledgePlan => {
  const shortfallUnits = amountToChainUnits(shares) - amountToChainUnits(unlockedShares);
  if (shortfallUnits <= 0) return { kind: "proceed" };
  // No pledge to blame: this is a plain over-withdraw, and the redeem's own
  // abort says so more accurately than a collateral notice would.
  if (!pledged || pledged.amount <= 0) return { kind: "proceed" };

  // A queued unpledge is NOT reserved against the pledge (`reservation_applies`
  // covers the primary asset only), so `pledged` still counts shares already on
  // their way out — and a retry would drain twice what the redemption needs.
  const remainingUnits = shortfallUnits - pendingUnpledgeUnits;
  if (remainingUnits <= 0) return { kind: "queued" };

  // Floored, so a partly-releasable pledge is never over-asked. The in-flight
  // amount is spoken for and cannot cover this request too.
  const releasableUnits = amountToChainUnits(pledged.withdrawable) - pendingUnpledgeUnits;
  if (releasableUnits < remainingUnits) return { kind: "locked" };
  return { kind: "unpledge", units: remainingUnits };
};

const MIN_DEPOSIT_AMOUNT = 10;

export interface DepositInputError {
  key: string;
  params: Record<string, number>;
}

export const getDepositInputError = (amount: number): DepositInputError | undefined => {
  if (amount < MIN_DEPOSIT_AMOUNT) {
    return { key: "minDepositAmount", params: { minDeposit: MIN_DEPOSIT_AMOUNT } };
  }

  return undefined;
};

export const updateVaultWithAccountOverview = (
  vault: WithSubaccount<Vault>,
  accountOverview: AccountOverview | undefined,
) => {
  if (accountOverview) {
    vault.pnl_90d = accountOverview.pnl_90d;
    vault.tvl = accountOverview.perp_equity_balance;
  }

  return { ...vault, all_time_earned: vault.all_time_pnl ?? getTotalPnl(accountOverview) };
};
